// 学生端专用 API 服务
// 使用环境变量或默认生产地址，确保与其他端一致
const API_BASE_URL = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || 'https://api.talknect.com'

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    name: string
    code: string
    message: string
    details?: any
  }
  message?: string
}

// 获取认证令牌
function getAuthToken(): string {
  const authData = localStorage.getItem('talknect-student-auth')
  if (authData) {
    try {
      const parsed = JSON.parse(authData)
      return parsed?.state?.token || parsed?.token || ''
    } catch {
      return ''
    }
  }
  return ''
}

// 处理认证失效
function handleAuthExpired() {
  localStorage.removeItem('talknect-student-auth')
  const currentPath = window.location.pathname
  if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
    window.location.href = '/login?expired=true'
  }
}

// 通用请求函数
async function request<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getAuthToken()
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> || {})
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  // 确保路径以 /api/v1 开头
  const apiPath = endpoint.startsWith('/api/v1') ? endpoint : `/api/v1${endpoint}`
  const url = `${API_BASE_URL}${apiPath}`
  
  try {
    const response = await fetch(url, {
      ...options,
      headers
    })
    
    // 处理认证失效
    if (response.status === 401 || response.status === 403) {
      console.warn('认证已失效，正在退出登录...')
      handleAuthExpired()
      return {
        success: false,
        error: {
          name: 'AuthError',
          code: 'AUTH_EXPIRED',
          message: '您已长时间未操作，请重新登录'
        }
      }
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('API request failed:', error)
    return {
      success: false,
      error: {
        name: 'NetworkError',
        code: 'NETWORK_ERROR',
        message: '网络错误，请检查网络连接'
      }
    }
  }
}

// 学生端 API 客户端
export const api = {
  get: <T = any>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
  
  post: <T = any>(endpoint: string, data?: any) => request<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  }),
  
  put: <T = any>(endpoint: string, data?: any) => request<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  }),
  
  delete: <T = any>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
  
  // 文件上传
  upload: async <T = any>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> => {
    const token = getAuthToken()
    const formData = new FormData()
    formData.append('file', file)
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
    }
    
    const apiPath = endpoint.startsWith('/api/v1') ? endpoint : `/api/v1${endpoint}`
    const url = `${API_BASE_URL}${apiPath}`
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      })
      
      if (response.status === 401 || response.status === 403) {
        handleAuthExpired()
        return {
          success: false,
          error: {
            name: 'AuthError',
            code: 'AUTH_EXPIRED',
            message: '您已长时间未操作，请重新登录'
          }
        }
      }
      
      return await response.json()
    } catch (error) {
      console.error('Upload failed:', error)
      return {
        success: false,
        error: {
          name: 'UploadError',
          code: 'UPLOAD_FAILED',
          message: '上传失败，请重试'
        }
      }
    }
  }
}

// 兼容 studentApi 命名
export const studentApi = api

export default studentApi
